
import React from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider } from '@/lib/auth-context'
import { RBACProvider } from '../lib/rbac/context'
import { AmplifyProvider } from '../contexts/amplify-context'
import { TenantProvider } from '../lib/tenant/tenant-provider'
import { ImpersonationProvider } from '../contexts/ImpersonationContext'
import { RoleOverrideProvider } from '../contexts/RoleOverrideContext'
import { queryClient } from '../config/queryClient'

interface ProvidersProps {
  children: React.ReactNode
}

/**
 * Simplified Provider Tree
 *
 * AuthProvider is now the SINGLE SOURCE OF TRUTH for user data
 * - User data comes from /users/me endpoint (includes employee data)
 * - ImpersonationProvider wraps AuthProvider to enable admin impersonation
 * - RoleOverrideProvider allows admins to test different role permissions
 * - RBACProvider reads from AuthProvider (or RoleOverrideProvider if overridden)
 * - QueryClientProvider enables React Query for server state management (with optimized caching)
 * - ReactQueryDevtools provides debugging tools in development mode
 */
export default function Providers({ children }: ProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AmplifyProvider>
        <TenantProvider>
          <AuthProvider>
            <ImpersonationProvider>
              <RoleOverrideProvider>
                <RBACProvider>
                  {children}
                </RBACProvider>
              </RoleOverrideProvider>
            </ImpersonationProvider>
          </AuthProvider>
        </TenantProvider>
      </AmplifyProvider>
      {/* React Query DevTools - only loads in development */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
} 